// src/CardTile.js
import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CardAttributePill, { ConditionPill, LanguagePill } from "./components/CardAttributePill";
import BrowseUserIdentityLink from "./components/BrowseUserIdentityLink";
import { formatCustomPriceTrendComparison, getCustomPriceTrendComparison } from "./utils/customPriceTrendComparison";
import normalizeCondition from "./utils/normalizeCondition";

function formatBadgeCurrency(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  return `${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} €`;
}

export default function CardTile({ card, badgeType, salePrice, secondaryListedBadge, traderLink, overlayAction = null, editMode = false, pendingEdits = {}, setPendingEdits, preferNormalImage = false, stackOverlayActionOnMobile = false, showCustomPriceComparison = false }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imageRef = useRef(null);

  const {
    image,
    language,
    condition: rawCondition,
    foil,
    qty,
    displayQty,
    price_trend,
    price_foil,
    isBrowseTradeBlocked,
    browseTradeBlockedLabel,
    tradeLockLabel,
    isPendingTradeLocked,
    isAcceptedTradeLocked
  } = card;

  const condition = normalizeCondition(rawCondition);
  const currentEdit = pendingEdits[card.id] || {};
  const selectedLanguage = currentEdit.language ?? language ?? "";
  const selectedCondition = currentEdit.condition ?? condition ?? "";
  const selectedFoil = currentEdit.foil ?? foil;
  const selectedAltered = currentEdit.altered ?? card.altered;
  const selectedSigned = currentEdit.signed ?? card.signed;
  const anyEdition = currentEdit.anyEdition ?? card.any_edition ?? card.anyEdition;
  const anyLanguage = currentEdit.anyLanguage ?? card.any_language ?? card.anyLanguage;
  const anyCondition = currentEdit.anyCondition ?? card.any_condition ?? card.anyCondition;
  const anyFoil = currentEdit.anyFoil ?? card.any_foil ?? card.anyFoil;
  const anyAltered = currentEdit.anyAltered ?? card.any_altered ?? card.anyAltered;
  const anySigned = currentEdit.anySigned ?? card.any_signed ?? card.anySigned;
  const customPriceComparison = showCustomPriceComparison && badgeType === "LISTED"
    ? getCustomPriceTrendComparison(card, salePrice)
    : null;
  const listedBadgeBackground = customPriceComparison?.tone === "warning" ? "#f97316" : "#16a34a";
  const wildcardLabels = [
    anyEdition ? "edition" : null,
    anyLanguage ? "language" : null,
    anyCondition ? "condition" : null,
    anyFoil ? "foil" : null,
    anyAltered ? "altered" : null,
    anySigned ? "signed" : null
  ].filter(Boolean);

  const tileImage = preferNormalImage
    ? (card.image || card.imageSmall || "")
    : (card.imageSmall || card.image || "");
  const imageSrc = tileImage || image || "";
  const hasTileImage = Boolean(imageSrc);
  const badgeTopOffset = "9%";
  const listedStackedBadgeTop = "22%";
  const listedSecondaryStackedBadgeTop = "32%";
  const badgeRightOffset = 1;
  const badgeLeftOffset = 15;
  const imageShell = (
    <div className={`card-tile-image-shell${isImageLoaded ? " is-loaded" : ""}`}>
      <div className="card-tile-image-placeholder" aria-hidden="true">
        <div className="card-tile-image-placeholder__frame">
          <div className="card-tile-image-placeholder__orb" />
          <div className="card-tile-image-placeholder__title">Loading</div>
          <div className="card-tile-image-placeholder__subtitle">...</div>
        </div>
      </div>
      {hasTileImage ? (
        <img
          ref={imageRef}
          src={imageSrc}
          alt=""
          loading="lazy"
          onLoad={() => setIsImageLoaded(true)}
          onError={() => setIsImageLoaded(true)}
          className={`card-tile-image${isImageLoaded ? " is-loaded" : ""}`}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            transform: "none"
          }}
        />
      ) : null}
      {(Number(displayQty ?? qty) || 0) > 0 ? (
        <div
          style={{
            position: "absolute",
            left: 6,
            bottom: 6,
            background: "#000000aa",
            color: "white",
            padding: "2px 6px",
            borderRadius: "6px",
            fontSize: "12px",
            zIndex: 10
          }}
        >
          x{displayQty ?? qty}
        </div>
      ) : null}
    </div>
  );

  useLayoutEffect(() => {
    setIsImageLoaded(false);

    if (!imageSrc) {
      return;
    }

    if (imageRef.current?.complete) {
      setIsImageLoaded(true);
    }
  }, [imageSrc]);

  return (
    <div
      className={stackOverlayActionOnMobile ? "card-tile card-tile--stack-overlay-mobile" : "card-tile"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isBrowseTradeBlocked ? "#d1d5db" : "white",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: isHovered ? "0 4px 8px rgba(0,0,0,0.2)" : "0 2px 6px rgba(0,0,0,0.15)",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        cursor: isBrowseTradeBlocked ? "not-allowed" : (editMode ? "default" : "pointer"),
        position: "relative",
        transform: isHovered ? "translateY(-4px)" : "translateY(0)",
        minHeight: "280px",
        opacity: isBrowseTradeBlocked ? 0.52 : 1,
        filter: isBrowseTradeBlocked ? "grayscale(1) saturate(0.2) contrast(0.9)" : "none"
      }}
    >
      {isBrowseTradeBlocked ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(107, 114, 128, 0.34)",
            zIndex: 11,
            pointerEvents: "none"
          }}
        />
      ) : null}

      {isBrowseTradeBlocked ? (
        <div style={{
          position: "absolute",
          top: badgeType === "LISTED" ? listedStackedBadgeTop : badgeTopOffset,
          left: badgeLeftOffset,
          background: "#475569",
          color: "white",
          padding: "4px 10px",
          borderRadius: "4px",
          fontSize: "10px",
          zIndex: 12,
          fontWeight: "bold",
          textAlign: "center",
          minWidth: "88px",
          lineHeight: 1.2,
          textTransform: "uppercase",
          letterSpacing: "0.04em"
        }}>
          {browseTradeBlockedLabel || "In suggestion"}
        </div>
      ) : null}
      {/* Badge rendering (UNLISTED, FOR TRADE, PRICE) */}
      {badgeType === "UNLISTED" && (
        <div style={{
          position: "absolute",
          top: badgeTopOffset,
          right: badgeRightOffset,
          background: "#ffa500",
          color: "white",
          padding: "4px 10px",
          borderRadius: "4px",
          fontSize: "11px",
          zIndex: 5,
          fontWeight: "bold",
          textAlign: "center",
          minWidth: "60px",
          lineHeight: 1.2
        }}>
          UNLISTED
        </div>
      )}
      {badgeType === "LISTED" && (
        <div style={{
          position: "absolute",
          top: badgeTopOffset,
          right: badgeRightOffset,
          zIndex: 5,
          display: "grid",
          justifyItems: "end",
          gap: "6px"
        }}>
          <div style={{
            background: listedBadgeBackground,
            color: "white",
            padding: "4px 10px",
            borderRadius: "4px",
            fontSize: "11px",
            fontWeight: "bold",
            textAlign: "center",
            minWidth: "60px",
            lineHeight: 1.2
          }}>
            FOR TRADE<br />
            <span style={{ fontSize: "13px", fontWeight: 700 }}>
              {formatBadgeCurrency(salePrice)}
            </span>
            {customPriceComparison ? (
              <>
                <br />
                <span style={{ fontSize: "11px", fontWeight: 700 }}>
                  {formatCustomPriceTrendComparison(customPriceComparison.percentDelta)}
                </span>
              </>
            ) : null}
          </div>
          {secondaryListedBadge ? (
            <div style={{
              background: "#f97316",
              color: "white",
              padding: "4px 10px",
              borderRadius: "4px",
              fontSize: "10px",
              fontWeight: "bold",
              textAlign: "center",
              minWidth: "88px",
              lineHeight: 1.2,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              boxShadow: "0 6px 14px rgba(249, 115, 22, 0.22)"
            }}>
              {secondaryListedBadge}
            </div>
          ) : null}
        </div>
      )}
      {tradeLockLabel ? (
        <div style={{
          position: "absolute",
          top: badgeType === "LISTED"
            ? (secondaryListedBadge ? listedSecondaryStackedBadgeTop : listedStackedBadgeTop)
            : badgeTopOffset,
          right: badgeRightOffset,
          background: isPendingTradeLocked ? "#ea580c" : (isAcceptedTradeLocked ? "#2563eb" : "#475569"),
          color: "white",
          padding: "4px 10px",
          borderRadius: "4px",
          fontSize: "10px",
          zIndex: 5,
          fontWeight: "bold",
          textAlign: "center",
          minWidth: "78px",
          lineHeight: 1.2,
          textTransform: "uppercase",
          letterSpacing: "0.04em"
        }}>
          {tradeLockLabel}
        </div>
      ) : null}
      {badgeType === "WANT" && (
        <div style={{
          position: "absolute",
          top: badgeTopOffset,
          right: badgeRightOffset,
          background: "#0f766e",
          color: "white",
          padding: "4px 10px",
          borderRadius: "4px",
          fontSize: "11px",
          zIndex: 5,
          fontWeight: "bold",
          textAlign: "center",
          minWidth: "60px",
          lineHeight: 1.2
        }}>
          WANT
        </div>
      )}
      {card.browseCardPath ? (
        <Link
          to={card.browseCardPath}
          aria-label={`Browse card ${card.card || card.name || "card"}`}
          onClick={(event) => event.stopPropagation()}
          style={{ display: "block", color: "inherit", textDecoration: "none" }}
        >
          {imageShell}
        </Link>
      ) : imageShell}

      <div
        className="card-tile__details"
        style={{
          padding: "10px",
          background: isBrowseTradeBlocked ? "#d1d5db" : (isHovered ? "#f8f9fa" : "white")
        }}
      >
        <div style={{ display: "flex", gap: "6px", rowGap: "4px", marginBottom: "4px", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
          {/* Language */}
          {!anyLanguage && selectedLanguage && (
            <LanguagePill language={selectedLanguage} />
          )}

          {/* Condition */}
          {!anyCondition && selectedCondition && (
            <ConditionPill condition={selectedCondition} />
          )}

          {/* Foil Badge */}
          {!anyFoil && selectedFoil && (
            <CardAttributePill type="foil" title="Foil" />
          )}

          {!anyAltered && selectedAltered && (
            <CardAttributePill type="altered" title="Altered" />
          )}

          {!anySigned && selectedSigned && (
            <CardAttributePill type="signed" title="Signed" />
          )}

          {/* Pris badge fjernet - vises kun på FOR TRADE label */}
        </div>

        <div
          title={wildcardLabels.length > 0 ? `Any for ${wildcardLabels.join(", ")}` : undefined}
          style={{
            marginBottom: "6px",
            fontSize: "11px",
            color: "#64748b",
            lineHeight: 1.3,
            whiteSpace: "normal"
          }}
        >
          {wildcardLabels.length > 0 ? `Any for ${wildcardLabels.join(", ")}` : "-"}
        </div>

        <div style={{ fontSize: "12px", lineHeight: "1.3" }}>
          <div>Trend: {price_trend ?? '—'} €</div>
          <div>Foil Trend: {price_foil ? price_foil : '—'} €</div>
          {traderLink ? (
            <div style={{ marginTop: "4px" }}>
              <BrowseUserIdentityLink
                to={traderLink.to}
                state={traderLink.state}
                onClick={() => {}}
                stopPropagation={true}
                username={traderLink.label}
                tier={traderLink.collectorTier}
                wrap={true}
                truncateAt={10}
              />
            </div>
          ) : null}
        </div>
      </div>

      {overlayAction ? (
        <div
          className={stackOverlayActionOnMobile ? "card-tile__overlay-action card-tile__overlay-action--stack-mobile" : "card-tile__overlay-action"}
          style={{
            position: "absolute",
            right: 8,
            bottom: 10,
            zIndex: 12
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {overlayAction}
        </div>
      ) : null}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @media (max-width: 640px) {
          .card-tile--stack-overlay-mobile .card-tile__overlay-action--stack-mobile {
            position: static !important;
            right: auto !important;
            bottom: auto !important;
            display: flex;
            justify-content: flex-end;
            margin: 0 10px 10px;
          }
        }
      `}</style>
    </div>
  );
}
